import React, { useState } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  Slide,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Business,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { usersAPI, apiUtils } from "../services/api";
import { APP_NAME, APP_DESCRIPTION } from "../constants/app";
import logo from "../assets/tienda-logo-alpha.png";
import loginBg from "../assets/login-bg.jpg";
import useResponsive from "../hooks/useResponsive";

const MobileLoginContainer = styled(Container)(({ theme, ismobile }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: ismobile === "true" ? "flex-start" : "center",
  minHeight: "100vh",
  minWidth: "100vw",
  width: "100%",
  padding: 0,
  margin: 0,
  backgroundColor: theme.palette.background.default,
  backgroundImage: `url(${loginBg})`,
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  backgroundAttachment: ismobile === "true" ? "scroll" : "fixed",
  backgroundPosition: "center center",
  position: "relative",
  paddingTop: ismobile === "true" ? theme.spacing(4) : 0,
  paddingBottom: ismobile === "true" ? theme.spacing(4) : 0,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: ismobile === "true" 
      ? "rgba(0, 0, 0, 0.6)" 
      : "rgba(0, 0, 0, 0.8)",
    zIndex: 0,
  },
}));

const MobileLoginPaper = styled(Paper)(({ theme, ismobile }) => ({
  padding: ismobile === "true" ? theme.spacing(3) : theme.spacing(4),
  width: "100%",
  maxWidth: ismobile === "true" ? "100%" : 400,
  margin: ismobile === "true" ? theme.spacing(2) : 0,
  textAlign: "center",
  position: "relative",
  zIndex: 1,
  backgroundColor: ismobile === "true" 
    ? "rgba(47, 46, 46, 0.85)" 
    : "rgba(47, 46, 46, 0.85)",
  borderRadius: ismobile === "true" ? theme.spacing(2) : theme.spacing(1),
  boxShadow: ismobile === "true" 
    ? "0 8px 32px rgba(0, 0, 0, 0.3)"
    : theme.shadows[3],
}));

const MobileLoginForm = styled("form")(({ theme, ismobile }) => ({
  display: "flex",
  flexDirection: "column",
  gap: ismobile === "true" ? theme.spacing(2.5) : theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const MobileTextField = styled(TextField)(({ theme, ismobile }) => ({
  "& .MuiOutlinedInput-root": {
    height: ismobile === "true" ? 56 : "auto",
    fontSize: ismobile === "true" ? "16px" : "14px", // Prevents zoom on iOS
    "& fieldset": {
      borderRadius: ismobile === "true" ? 12 : 4,
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: ismobile === "true" ? "16px" : "14px",
  },
}));

const MobileButton = styled(Button)(({ theme, ismobile }) => ({
  height: ismobile === "true" ? 56 : 48,
  fontSize: ismobile === "true" ? "16px" : "14px",
  borderRadius: ismobile === "true" ? 12 : 4,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: ismobile === "true" 
    ? "0 4px 12px rgba(0, 0, 0, 0.2)"
    : theme.shadows[2],
  "&:active": {
    transform: ismobile === "true" ? "scale(0.98)" : "none",
  },
}));

const LogoImage = styled("img")(({ theme, ismobile }) => ({
  height: ismobile === "true" ? "48px" : "64px",
  width: "auto",
  marginBottom: theme.spacing(2),
}));

const MobileLogin = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
    tenant_code: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [slideIn, setSlideIn] = useState(false);

  const { isMobile, screenSize } = useResponsive();

  React.useEffect(() => {
    // Add slide-in animation
    const timer = setTimeout(() => setSlideIn(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
    // Clear error when user starts typing
    if (error) setError("");
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate fields
    if (!credentials.tenant_code.trim()) {
      setError("Please enter tenant code");
      setLoading(false);
      return;
    }
    if (!credentials.username.trim()) {
      setError("Please enter username");
      setLoading(false);
      return;
    }
    if (!credentials.password.trim()) {
      setError("Please enter password");
      setLoading(false);
      return;
    }

    try {
      const response = await usersAPI.login(credentials);
      const formatted = apiUtils.formatResponse(response);
      const userData = Array.isArray(formatted) && formatted.length > 0
        ? formatted[0]
        : formatted;

      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData));
        onLogin(userData);
      } else {
        setError("Login failed. Invalid credentials.");
      }
    } catch (err) {
      console.error("Login error:", err);
      const friendly = apiUtils.handleError(err);
      setError(friendly);

      // Fallback to demo login if API fails
      if (
        credentials.username === "admin" &&
        credentials.password === "admin"
      ) {
        const user = {
          id: 1,
          username: "admin",
          role: "admin",
          entity_name: "Demo Store",
        };
        localStorage.setItem("user", JSON.stringify(user));
        onLogin(user);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <MobileLoginContainer ismobile={isMobile.toString()}>
      <Slide direction="up" in={slideIn} timeout={500}>
        <MobileLoginPaper elevation={3} ismobile={isMobile.toString()}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 2,
            }}
          >
            <LogoImage
              src={logo}
              alt={`${APP_NAME} Logo`}
              ismobile={isMobile.toString()}
            />
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: isMobile ? "text.primary" : "inherit",
              }}
            >
              {APP_NAME}
            </Typography>
            <Typography 
              variant={isMobile ? "body1" : "h6"} 
              color="text.secondary" 
              gutterBottom
              sx={{
                fontSize: isMobile ? "14px" : "inherit",
              }}
            >
              {APP_DESCRIPTION}
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2,
                borderRadius: isMobile ? 2 : 1,
                fontSize: isMobile ? "14px" : "inherit",
              }}
            >
              {error}
            </Alert>
          )}

          <MobileLoginForm onSubmit={handleSubmit} ismobile={isMobile.toString()}>
            <MobileTextField
              name="tenant_code"
              label="Tenant Code"
              value={credentials.tenant_code}
              onChange={handleChange}
              required
              fullWidth
              autoFocus={!isMobile} // Avoid auto-focus on mobile to prevent zoom
              ismobile={isMobile.toString()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Business color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <MobileTextField
              name="username"
              label="Username"
              value={credentials.username}
              onChange={handleChange}
              required
              fullWidth
              ismobile={isMobile.toString()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <MobileTextField
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              value={credentials.password}
              onChange={handleChange}
              required
              fullWidth
              ismobile={isMobile.toString()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      size={isMobile ? "large" : "medium"}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <MobileButton
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              ismobile={isMobile.toString()}
              sx={{ mt: 1 }}
            >
              {loading ? "Signing in..." : "Sign In"}
            </MobileButton>
          </MobileLoginForm>
        </MobileLoginPaper>
      </Slide>
    </MobileLoginContainer>
  );
};

export default MobileLogin;